// app/src/lib/ocr/reconcile.js — arithmetic sanity checks for OCR'd invoices.
//
// OCR sometimes mis-reads a column — most damagingly it can pick up a vendor's
// product/item NUMBER as the quantity (e.g. item "35.120" parsed as qty 35.12),
// which then produces a fabricated line total. The tell is always the same: the
// printed numbers stop agreeing with each other. These pure helpers surface that
// so the review screen can flag it instead of silently trusting a bad parse.
//
// Two independent checks:
//   1. Per line — does the PRINTED extended price equal quantity × unitPrice?
//   2. Whole invoice — does the sum of line totals (+ tax + freight − discount)
//      equal the PRINTED invoice total?
// We never "fix" the numbers by overwriting one with our own calculation; we
// report the disagreement and let the operator correct the misread field.
//
// All amounts are integer cents.

// Per-line slack for the qty × unitPrice rounding (unit price is stored to the
// cent, so a single line can be off by a cent or so legitimately).
const LINE_TOLERANCE_CENTS = 2;

/** Expected line total from quantity × unit price, in cents. */
export function expectedLineCents(quantity, unitPriceCents) {
  return Math.round((Number(quantity) || 0) * (Number(unitPriceCents) || 0));
}

/**
 * Does a line's PRINTED extended price agree with quantity × unitPrice?
 * Returns true when there is nothing to contradict (no printed extended — e.g.
 * the operator just edited qty/unit, which clears it) or they agree within
 * tolerance; false when the printed total and qty × unit disagree, which usually
 * means a column was mis-read.
 *
 * Bulk / catch-weight lines are exempt: a sack of onions or case of eggs is
 * billed by actual weight, so the printed line total intentionally won't equal
 * the container count × the per-pound price. Once the operator marks a line as
 * sold in bulk we trust the printed total and stop flagging it.
 *
 * A per-line `discountCents` is honoured too: vendors often list a discount in
 * parentheses on the following line, so the printed total is qty × unit MINUS
 * that discount. Once the discount is recorded the line reconciles again.
 * @param {{ quantity?:number, unitPriceCents?:number, extPriceCents?:number|null, bulk?:boolean, discountCents?:number }} line
 */
export function lineReconciles(line, toleranceCents = LINE_TOLERANCE_CENTS) {
  if (!line || line.extPriceCents == null) return true;
  if (line.bulk) return true;
  const expected = expectedLineCents(line.quantity, line.unitPriceCents) - (line.discountCents || 0);
  return Math.abs(line.extPriceCents - expected) <= toleranceCents;
}

/**
 * Reconcile a whole OCR draft. The draft carries `lines` (each with quantity,
 * unitPriceCents and optional printed extPriceCents), tax/freight/discount in
 * cents, and the PRINTED invoice total (`printedTotalCents`, null when the
 * document didn't state one).
 *
 * @param {{ lines?: Array, taxCents?:number, freightCents?:number, discountCents?:number, printedTotalCents?:number|null }} draft
 * @param {{ lineToleranceCents?:number, invoiceToleranceCents?:number }} [opts]
 * @returns {{
 *   lineOk: boolean[], anyLineSuspect: boolean,
 *   computedSubtotalCents: number, computedTotalCents: number,
 *   printedTotalCents: number|null, totalsKnown: boolean,
 *   totalsReconcile: boolean, deltaCents: number
 * }}
 */
export function reconcileInvoice(draft, opts = {}) {
  const lineTol = opts.lineToleranceCents ?? LINE_TOLERANCE_CENTS;
  const lines = (draft && draft.lines) || [];
  const lineOk = lines.map((l) => lineReconciles(l, lineTol));
  // Mirror the screen's own line-total logic: trust a printed extended when we
  // have one, otherwise fall back to quantity × unit price less any discount.
  const computedSubtotalCents = lines.reduce((s, l) =>
    s + (l.extPriceCents != null
      ? l.extPriceCents
      : Math.max(0, expectedLineCents(l.quantity, l.unitPriceCents) - (l.discountCents || 0))), 0);
  // `otherCents` is the operator's own adjustment (see lib/ocr/invoiceTotals.js).
  // It belongs in the computed total, otherwise correcting the total to what the
  // invoice actually says would itself raise a "totals don't agree" warning.
  const computedTotalCents = computedSubtotalCents
    + (draft.taxCents || 0) + (draft.freightCents || 0) + (draft.otherCents || 0)
    - (draft.discountCents || 0);
  const printedTotalCents = draft.printedTotalCents != null ? draft.printedTotalCents : null;
  const totalsKnown = printedTotalCents != null;
  // The invoice tolerance absorbs per-line rounding accumulated across the lines
  // we had to compute (those without a printed extended).
  const invTol = opts.invoiceToleranceCents ?? (lineTol + lines.length);
  const deltaCents = totalsKnown ? computedTotalCents - printedTotalCents : 0;
  const totalsReconcile = !totalsKnown || Math.abs(deltaCents) <= invTol;
  return {
    lineOk,
    anyLineSuspect: lineOk.some((ok) => !ok),
    computedSubtotalCents,
    computedTotalCents,
    printedTotalCents,
    totalsKnown,
    totalsReconcile,
    deltaCents,
  };
}
